import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { ChangeFailRateResult } from '../models/team-metrics';
import { TeamMetricsService } from './team-metrics.service';
import { ReleaseReportReleasesResponse } from '../models/release';

@Injectable({
  providedIn: 'root'
})
export class ChangeFailRateCalculatorService {

  constructor(
    private readonly teamMetricsService: TeamMetricsService
  ) { }

  /**
   * Calculates Change Fail Rate metric, potentially including comparison data.
   */
  public calculate(
    teamName: string,
    startDate: string,
    endDate: string,
    _org: string,
    _project: string,
    includeComparison: boolean = true
  ): Observable<ChangeFailRateResult> {
    if (includeComparison) {
      return this.teamMetricsService.addComparisonToMetric(
        (team, start, end) => this.calculateSinglePeriod(team, start, end),
        teamName,
        startDate,
        endDate,
        true,
        'changeFailRate'
      );
    }
    return this.calculateSinglePeriod(teamName, startDate, endDate);
  }

  /**
   * Core logic for calculating Change Fail Rate for a single period.
   * This will eventually house the logic from the original calculateChangeFailRateCore.
   */
  private calculateSinglePeriod(
    teamName: string,
    startDate: string,
    endDate: string
  ): Observable<ChangeFailRateResult> {
    return this.teamMetricsService.ensureOwnershipDataLoaded().pipe(
      switchMap(() => this.teamMetricsService.getSmartReleaseData(startDate, endDate)),
      map((fetchedReleasesInRange: ReleaseReportReleasesResponse[]) => {
        const teamReleases = this.teamMetricsService.filterReleasesByTeam(fetchedReleasesInRange, teamName);
        
        if (teamReleases.length === 0) {
          return {
            value: 0,
            displayValue: 'N/A',
            subtitle: 'No releases found',
            totalReleases: 0,
            failedReleases: 0,
            period: this.teamMetricsService.formatDateRange(startDate, endDate)
          };
        }

        const failedReleases = teamReleases.filter(release => {
          const changeFail = this.determineChangeFail(release);
          return !changeFail;
        });

        const totalReleases = teamReleases.length;
        const failedCount = failedReleases.length;
        const changeFailRate = totalReleases > 0 ? (failedCount / totalReleases) * 100 : 0;
        
        console.log(`[CFR Calc Service] Team: ${teamName}, Period: ${this.teamMetricsService.formatDateRange(startDate, endDate)}`);
        console.log(`[CFR Calc Service] Total Releases: ${totalReleases}, Failed Releases: ${failedCount}, Rate: ${changeFailRate.toFixed(1)}%`);

        return {
          value: changeFailRate,
          displayValue: `${changeFailRate.toFixed(1)}%`,
          subtitle: `${failedCount} of ${totalReleases} releases`,
          totalReleases,
          failedReleases: failedCount,
          period: this.teamMetricsService.formatDateRange(startDate, endDate)
        };
      }),
      catchError(error => {
        console.error(`[CFR Calc Service] Error calculating for team ${teamName} between ${startDate} and ${endDate}:`, error);
        return of({
          value: 0,
          displayValue: 'N/A',
          subtitle: 'Error retrieving data',
          totalReleases: 0,
          failedReleases: 0,
          period: this.teamMetricsService.formatDateRange(startDate, endDate)
        });
      })
    );
  }

  /**
   * Determines if a release is a change failure.
   * If ChangeFail property is explicitly true or false, that value is used.
   * Otherwise, falls back to determineReleaseStatus.
   * @param release The release data.
   * @returns True if the release is a change failure (i.e., should be counted as failed for CFR), false otherwise.
   */
  private determineChangeFail(release: ReleaseReportReleasesResponse): boolean {
    if (release.Release.ChangeFail === null || release.Release.ChangeFail === undefined) {
      return this.determineReleaseStatus(release.Release);
    }
    return release.Release.ChangeFail;
  }

  /**
   * Determines release status based on the Success property.
   * Considers null Success as successful.
   * @param release The release data (specifically the Release.Release part).
   * @returns True if successful, false if failed.
   */
  private determineReleaseStatus(release: any): boolean {
    if (release.Success === null) {
      return true; 
    }
    return release.Success;
  }
}
